import { SimpleLocation } from "../google-geocoding/google-geocoding.types";
import { CmsNameValueDTO } from "../cms/cms-api.types";
import { CmsNameValue } from "../cms/cms.types";
import { SalaryExpectation } from "../../../../../common-ui/src/lib/salary-expectation-input/salary-expectation.type";

export interface JobOffer {
  id: string;
  missionPartnerId: string;
  forMissionPartnerId?: string;
  missionPartnerName: string;
  archived: boolean;
  published?: boolean;
  name: string;
  description: string;
  salaryExpectations?: SalaryExpectation;
  locations: Array<SimpleLocation>;
  workingModes?: Array<CmsNameValue>;
  jobSharing: boolean;
  domainExpertise: Array<CmsNameValue>;
  workExperience?: number;
  leadershipExperience?: number;
  leadershipTeamSize?: number;
  sectorFocus: Array<CmsNameValue>;
  workLanguage?: Array<CmsNameValue>;
  spokenLanguages?: Array<CmsNameValue>;
  matchedAmount: number;
  industries?: Array<CmsNameValue>;
  department?: Array<CmsNameValue>;
  careerLevel?: Array<CmsNameValue>;
  degree?: Array<CmsNameValue>;
  momSkills?: Array<CmsNameValue>;
  hashTags?: Array<string>;
  status: JobOpportunitySearchResultStatus;
  creationTimestamp: number;
  countryCode?: string;
  stage?: number;
  maxScore?: number;
  maxScoreExplanation?: string;
}

export interface JobOfferLocationDTO {
  postalCode: string;
  country: string;
  city: string;
  radius: CmsNameValueDTO | undefined;
}

export interface JobOfferDTO {
  id: string;
  missionPartnerId: { internalId: string };
  forMissionPartnerId?: { internalId: string };
  missionPartnerName: string;
  archived: boolean;
  published?: boolean;
  name: string;
  description: string;
  salaryMin?: number;
  salaryMax?: number;
  locations: Array<JobOfferLocationDTO>;
  workingModes?: Array<CmsNameValueDTO>;
  jobSharing: boolean;
  domainExpertise: Array<CmsNameValueDTO>;
  workExperience?: number;
  spokenLanguages?: Array<CmsNameValue>;
  leadershipExperience?: number;
  leadershipTeamSize?: number;
  sectorFocus: Array<CmsNameValueDTO>;
  workLanguage?: Array<CmsNameValueDTO>;
  matchedAmount: number;
  industries?: Array<CmsNameValueDTO>;
  department?: Array<CmsNameValueDTO>;
  careerLevel?: Array<CmsNameValueDTO>;
  degree?: Array<CmsNameValueDTO>;
  status: JobOpportunitySearchResultStatus;
  creationTimestamp: number;
  countryCode?: string;
}

export interface MatchedTalent {
  accountId: string;
  avatarUrl: string;
  salutation: string;
  firstName: string;
  lastName: string;
  jobTitle?: string;
  status: Array<MatchedTalentStatus>;
  salaryExpectations?: SalaryExpectation;
  salaryExpectationMatch: boolean;
  domainExpertiseMatch: Array<CmsNameValue>;
  domainExpertiseOther: Array<CmsNameValue>;
  industriesMatch: Array<CmsNameValue>;
  industriesOther: Array<CmsNameValue>;
  sectors?: Array<CmsNameValue>;
  sectorsMatch?: Array<CmsNameValue>;
  sectorsOther?: Array<CmsNameValue>;
  department?: Array<CmsNameValue>;
  departmentMatch?: Array<CmsNameValue>;
  departmentOther?: Array<CmsNameValue>;
  jobSharing: boolean;
  careerLevelMatch: boolean;
  careerLevel: CmsNameValue;
  willingnessToTravel: number;
  workExperience: number;
  workExperienceMatch: boolean;
  idealWorkingAssignments: Array<CmsNameValue>;
  idealWorkingAssignmentsMatch: Array<CmsNameValue>;
  idealWorkingAssignmentsOther: Array<CmsNameValue>;
  leadershipExperience: number;
  leadershipExperienceMatch: boolean;
  leadershipTeamSize: number;
  leadershipTeamSizeMatch: boolean;
  locationPostalCode: string;
  locationCity: string;
  locationCountry: string;
  locationMatch: boolean;
  deanonymized: boolean;
  radius: CmsNameValue;
  score: number;
  conversationId?: string;
  registrationTimestamp: number;
  hasComments: boolean;
  momSkills: Array<CmsNameValue>;
  hashTags: Array<string>;
  favourite: boolean;
  stage: number;
  handpicked: boolean;
  degree: CmsNameValue;
  degreeMatch: boolean;
  gender?: CmsNameValue;
  changeDate?: CmsNameValue;
  crossRegistered: boolean;
  crossRegisteredAccountId?: string;
}

export interface MatchedTalentDTO {
  accountId: {
    internalId: string;
  };
  avatar: {
    id: string;
    accountId: {
      internalId: string;
    };
    avatarUrl: string;
    name: string;
  };
  salutation: string;
  firstName: string;
  lastName: string;
  jobTitle?: string;
  status: Array<MatchedTalentStatus>;
  salaryExpectationMin: number;
  salaryExpectationMax: number;
  salaryExpectationMatch: boolean;
  domainExpertise: Array<CmsNameValueDTO>;
  domainExpertiseMatch: Array<CmsNameValueDTO>;
  industries: Array<CmsNameValueDTO>;
  industriesMatch: Array<CmsNameValueDTO>;
  sectors?: Array<CmsNameValueDTO>;
  sectorsMatch?: Array<CmsNameValueDTO>;
  department?: Array<CmsNameValueDTO>;
  departmentMatch?: Array<CmsNameValueDTO>;
  jobSharing: boolean;
  careerLevelMatch: boolean;
  careerLevel: CmsNameValueDTO;
  willingnessToTravel: number;
  workExperience: number;
  workExperienceMatch: boolean;
  idealWorkingAssignments: Array<CmsNameValueDTO>;
  idealWorkingAssignmentsMatch?: Array<CmsNameValueDTO> | boolean | null;
  leadershipExperience: number;
  leadershipExperienceMatch: boolean;
  leadershipTeamSize: number;
  leadershipTeamSizeMatch: boolean;
  locationPostalCode: string;
  locationCity: string;
  locationCountry: string;
  locationMatch: boolean;
  deanonymized: boolean;
  radius: CmsNameValueDTO;
  score: number;
  conversationId?: string;
  registrationTimestamp: number;
  hasComments: boolean;
  momSkills: Array<CmsNameValueDTO>;
  hashTags: Array<string>;
  favourite: boolean;
  stage: number;
  handpicked: boolean;
  degree: CmsNameValueDTO;
  degreeMatch: boolean;
  gender?: CmsNameValueDTO;
  crossRegistered: boolean;
  crossRegisteredAccountId?: string;
}

export interface JobOfferTalentCommentDTO {
  id: string;
  targetAccountId: {
    internalId: string;
  };
  jobOpportunityId: string;
  authorAccountId: {
    internalId: string;
  };
  text: string;
  creationTimestamp: number;
  authorName: string;
}

export interface JobOfferTalentComment {
  id: string;
  targetAccountId: {
    internalId: string;
  };
  jobOpportunityId: string;
  authorAccountId: {
    internalId: string;
  };
  text: string;
  creationTimestamp: string;
  authorName: string;
}

export interface StageUpdateEvent {
  jobOffer: JobOffer;
  stage: number;
}

export interface TalentStages {
  accountId: string;
  jobOpportunityId: string;
  stage: number;
  jobOpportnityName: string;
}

export enum MatchedTalentStatus {
  /**
   * Talent is new in matching result
   */
  UNSEEN = "UNSEEN",

  /**
   * There is communication between Mission Partner and Talent
   */
  IN_COMMUNICATION = "IN_COMMUNICATION",

  /**
   * Mission Partner and Talent have an agreement on Job Opportunity
   */
  CLOSED = "CLOSED",

  /**
   * Talent rejected to show their profile
   */
  REJECTED = "REJECTED",

  /**
   * Archived means that Mission Partner doesn't want to see this Talent in matching list
   */
  ARCHIVED = "ARCHIVED",
}

export enum JobOpportunitySearchResultStatus {
  PENDING = "PENDING",
  DONE = "DONE",
}

export interface JobOpportunityOverviewFilter {
  paging: {
    page: number;
    itemsOnPage: number;
  };
  searchTerm?: string;
  sort?: Array<{
    field: string;
    order: "ASC" | "DESC";
  }>;
  filters?: Array<{
    field: string;
    value: string;
  }>;
}

export interface JobOpportunityOverviewFilterDTO {
  paging: {
    page: number;
    itemsOnPage: number;
  };
  searchTerm?: string;
  sort?: Array<{
    field: string;
    order: "ASC" | "DESC";
  }>;
  filters?: Array<{
    field: string;
    value: string;
  }>;
}
